// src/hooks/useCsvExport.ts
//
// Single hook point for the «Exportar» button in every list page's fixed
// header cluster (Decision 9 / unit F of wave 1). Client-side CSV of the
// currently loaded rows today; the same call site can switch to the server's
// `format=csv` once the data provider exposes it (see `fetchServerCsv`).
//
// Format (Decision 9): `;` separator, UTF-8 BOM, es-ES headers taken from
// the same i18n keys the columns already use, filename
// `insight-<page name>-<from>-<to>.csv` (the page name in the user's language).
import { useCallback } from 'react';
import { useTranslate } from 'react-admin';
import { DASH } from '@/lib/analyticsFormat';

export interface CsvColumn<T> {
  /** i18n key for the header — reuse the exact key the datagrid column already uses. */
  labelKey: string;
  /**
   * Extract the cell value for a given row: text already formatted for
   * display, or a plain number (amounts, counts, percentages) so the
   * spreadsheet can add it up.
   */
  value: (row: T) => string | number | null | undefined;
}

export interface UseCsvExportOptions {
  /** Page slug used in the filename, e.g. 'reservations', 'check-ins'. */
  page: string;
  /**
   * Optional: call the API's `format=csv` export instead of building the CSV
   * client-side, once the endpoint supports it. When it throws/rejects, the
   * caller falls back to the client-side export of the rows it already has.
   */
  fetchServerCsv?: () => Promise<Blob>;
}

const BOM = '﻿';

/**
 * A number is written as a spreadsheet reads it in the user's language: no
 * thousands separator, the decimal comma in Spanish («457,37»), so it opens
 * as a number — never «457.37» or «457,37 €», which open as text.
 */
export const formatNumberCell = (value: number, locale: string): string =>
  Number.isFinite(value) ? value.toLocaleString(locale === 'en' ? 'en-GB' : 'es-ES', { useGrouping: false, maximumFractionDigits: 6 }) : '';

/** The «—» placeholder is for the screen; in a file an empty value is an empty cell. */
export const escapeCell = (value: string | number | null | undefined, locale: string): string => {
  const text =
    value === null || value === undefined || value === DASH ? '' : typeof value === 'number' ? formatNumberCell(value, locale) : String(value);
  if (text.includes(';') || text.includes('"') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const download = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Returns an `exportCsv(rows, columns, range?)` function. Columns are passed
 * at call time (not baked into the hook) so pages whose columns are only
 * known once data has loaded — or that reuse one hook instance across
 * several resources, like the admin options page — can still call it as a
 * plain function from an event handler.
 */
export function useCsvExport({ page, fetchServerCsv }: UseCsvExportOptions) {
  const translate = useTranslate();

  return useCallback(
    async <T>(rows: T[], columns: CsvColumn<T>[], range?: { from?: string; to?: string }) => {
      // «insight-reservas-2026-09-23-2026-09-29.csv»; a missing end of the
      // range is left out rather than written as an empty part.
      const name = translate(`common.exportFiles.${page}`, { _: page });
      const parts = ['insight', name, range?.from, range?.to].filter((part): part is string => Boolean(part));
      const filename = `${parts.join('-')}.csv`;

      if (fetchServerCsv) {
        try {
          const blob = await fetchServerCsv();
          download(blob, filename);
          return;
        } catch {
          // Endpoint not available yet — fall through to the client-side export below.
        }
      }

      if (!rows.length) return;

      const locale = translate('common.locale');
      const headerRow = columns.map((c) => escapeCell(translate(c.labelKey), locale)).join(';');
      const dataRows = rows.map((row) => columns.map((c) => escapeCell(c.value(row), locale)).join(';'));
      const csv = [headerRow, ...dataRows].join('\r\n');

      download(new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' }), filename);
    },
    [page, fetchServerCsv, translate]
  );
}
